import { v4 as uuidv4 } from 'uuid';
import { splitTextToFit, sanitizeHtml, htmlToText, textToHtml } from './textUtils';
import { storyPlainText, splitHtmlToFit, splitStoryHtml, richFlowFor, flowHtmlKey, flowHtmlOf, storyPrefix, splitHtmlAt, splitOptsFor } from './storyEdit';
import { documentObjects, locateObject, storyHeads, isTextType } from './storyChain';
import { buildTextStyles, getDisplayHtml } from '../components/editor/ObjectRenderer';
import { hasWordArtTransform } from './wordart';
import { layoutTextBox, needsEngine, isEngineEligible } from './textLayout';
import { computeWrapObstacles } from './textWrap';
import { tabOptionsOf } from './tabLayout';

/**
 * Story flow across pages (Publisher: one story, many boxes, any pages).
 *
 * On the canvas each rendered text box measures itself and records what flows on to the next box in `state.textFlow`
 * (CanvasObject). Boxes on *other* pages are not rendered, so their share of the story is measured here, off-screen.
 * Both use `measureBoxOverflow` — the one measurement — so a story never re-splits when its page is opened (words that
 * were at the bottom of box 1 must not reappear at the top of box 2). The same measurement drives Autoflow.
 */

export const boxPads = (props = {}) => ({
  t: props.paddingTop ?? props.padding ?? 4,
  r: props.paddingRight ?? props.padding ?? 4,
  b: props.paddingBottom ?? props.padding ?? 4,
  l: props.paddingLeft ?? props.padding ?? 4,
});

/** The wrap obstacles of a box that is not on the canvas: the objects of its page plus the scratch area, like the canvas. */
export const obstaclesOfBox = (box, pages = [], scratch = null) => {
  const loc = locateObject(box.id, pages, scratch);
  const own = loc && !loc.scratch ? pages[loc.pageIndex]?.objects || [] : [];
  const all = [...own, ...(scratch?.objects || [])];
  return all.length > 1 ? computeWrapObstacles(box, all, boxPads(box.properties || {})) : [];
};

/**
 * What flows out of `box` when `incoming` flows into it: { overflow, overflowHtml } — the plain text that does not fit
 * ('' when everything fits) and, when the text is formatted, the same overflow as HTML (see storyEdit.flowHtmlKey).
 * `obstacles` are the box's wrap obstacles (lib/textWrap.js); with obstacles — or Align to Baselines / vertical text —
 * the layout engine measures (it is what draws the box then), otherwise the browser does. The head is measured with
 * its own (rich) text — a headline in a larger font takes more room than the base font suggests —, a continuation
 * with the formatted overflow of the previous box (`incomingHtml`) when there is one, with plain text otherwise.
 * Shrink / best-fit text never overflows, a growing box grows, WordArt stretches to the frame.
 */
export const measureBoxFlow = (box, incoming, { obstacles = [], incomingHtml = '' } = {}) => {
  const props = box?.properties || {};
  const none = { overflow: '', overflowHtml: '' };
  if (!box || !isTextType(box) || !incoming || !incoming.trim()) return none;
  if (['shrink', 'best', 'grow'].includes(props.autofit) || hasWordArtTransform(props)) return none;
  const pads = boxPads(props);
  const availW = Math.max(1, box.width - pads.l - pads.r - (props.indentLeft || 0) - (props.indentRight || 0));
  const availH = Math.max(1, box.height - pads.t - pads.b);
  const { text: style, props: merged } = buildTextStyles(props, 1);
  // the head is measured with its own text; a continuation — or an empty box Autoflow is about to fill — with the
  // text flowing into it
  const own = !props.linkPrevId && storyPlainText(props) === incoming;
  const richHtml = own ? (props.html ? sanitizeHtml(props.html) : '') : richFlowFor(incomingHtml, incoming);
  let overflow;
  if ((obstacles.length > 0 || needsEngine(merged)) && isEngineEligible(merged)) {
    overflow = layoutTextBox(merged, richHtml || getDisplayHtml(props, own ? undefined : incoming), Math.max(1, box.width - pads.l - pads.r), availH, obstacles, { fontScale: 1, originY: box.y + pads.t }).overflowText;
  } else {
    const measureStyle = { ...style, transform: undefined, width: undefined };
    const tabs = tabOptionsOf(merged, 1);
    overflow = richHtml ? splitHtmlToFit(measureStyle, richHtml, availW, availH, tabs).overflowText : splitTextToFit(measureStyle, incoming, availW, availH, tabs).overflowText;
  }
  if (!overflow || !overflow.trim()) return none;
  const parts = richHtml ? splitStoryHtml(richHtml, incoming, overflow, splitOptsFor(merged)) : null;
  return { overflow, overflowHtml: parts ? parts.overflowHtml : '' };
};

/** The plain text that does not fit in `box` when `incoming` flows into it ('' when everything fits) — see measureBoxFlow. */
export const measureBoxOverflow = (box, incoming, opts) => measureBoxFlow(box, incoming, opts).overflow;

/**
 * The live flow of a linked box *while it is being edited* (Publisher: the words you type past the bottom of a box
 * appear in the next box at once, and the caret follows them). `win` is the box's edit window (storyEdit.editWindowFor),
 * `storyHtml` the story as it now stands (the head's HTML after the edit), `windowHtml` the editable's (cleaned) HTML.
 * Returns { overflow, overflowHtml, fitLen, clipAt, overflows }:
 *  - overflow / overflowHtml: what the box now sends on. The flow only grows during editing — words the window no
 *    longer holds; the words that already flowed on stay in the next box (the editable cannot show them), so an edit
 *    that would pull words back does that when editing ends (CanvasObject's measurement).
 *  - fitLen: offset, in the editable's text, where the box's room ends; clipAt: the end of the last word that fits;
 *    overflows: the editable holds text past it.
 * Only the *window* is measured (the browser path): its lines are the lines the whole story would get in this box, so
 * the split found inside it is the one the shared measurement finds on exit — and a 3,000-word story costs no more per
 * keystroke than a short one. With wrap obstacles / the layout engine the engine measures the incoming text as usual.
 */
export const liveFlowOf = (win, box, storyHtml, windowHtml, { obstacles = [] } = {}) => {
  const props = box?.properties || {};
  const story = htmlToText(sanitizeHtml(storyHtml || ''));
  const head = win.kind === 'head';
  const start = head ? 0 : win.prefix.length;
  const incoming = head ? story : story.slice(start).replace(/^\s+/, '');
  const opts = splitOptsFor(props);
  const incomingHtml = head ? '' : (splitHtmlAt(storyHtml, start, opts) || {}).tailHtml || '';
  const windowClean = sanitizeHtml(windowHtml || '');
  const windowPlain = htmlToText(windowClean);
  const windowLen = windowPlain.replace(/\s+$/, '').length;
  const { text: style, props: merged } = buildTextStyles(props, 1);
  const rich = head ? sanitizeHtml(storyHtml) : richFlowFor(incomingHtml, incoming);
  const beyond = incoming.slice(windowLen).trim(); // words flowing on past the window (the box's tail)
  let overflow = '';
  let pullTo = -1; // chars of `incoming` that fit when the window has room and words wait in the next box (pull-back)
  if (['shrink', 'best', 'grow'].includes(props.autofit) || hasWordArtTransform(props)) overflow = '';
  else if ((obstacles.length > 0 || needsEngine(merged)) && isEngineEligible(merged)) {
    const boxNow = head ? { ...box, properties: { ...props, html: sanitizeHtml(storyHtml), text: story } } : box;
    overflow = measureBoxFlow(boxNow, incoming, { obstacles, incomingHtml }).overflow; // the engine measures the whole incoming text
    if (beyond) pullTo = overflow ? storyPrefix(incoming, overflow).length : incoming.length;
  } else {
    const pads = boxPads(props);
    const availW = Math.max(1, box.width - pads.l - pads.r - (props.indentLeft || 0) - (props.indentRight || 0));
    const availH = Math.max(1, box.height - pads.t - pads.b);
    const measureStyle = { ...style, transform: undefined, width: undefined };
    const tabs = tabOptionsOf(merged, 1);
    const inWindow = splitHtmlToFit(measureStyle, windowClean, availW, availH, tabs).overflowText; // the window's own overflow
    if (inWindow && inWindow.trim()) {
      const fit = storyPrefix(windowPlain, inWindow).length;
      overflow = incoming.slice(fit).replace(/^\s+/, '');
    } else if (beyond) {
      // the window fits and words wait in the next box: after a deletion some of them may fit back here. Measured on
      // the formatted text from the window's start — galloping (textUtils.fitPrefixCount), so never the whole story.
      const back = splitHtmlToFit(measureStyle, rich || textToHtml(incoming), availW, availH, tabs).overflowText;
      pullTo = back && back.trim() ? storyPrefix(incoming, back).length : incoming.length;
    }
  }
  const fitLen = overflow ? storyPrefix(incoming, overflow).length : incoming.length;
  // where the editable's visible part ends: after the last word that fits (the whitespace before the first word that
  // flows on would put the end on the first clipped line)
  const clipAt = incoming.slice(0, fitLen).replace(/\s+$/, '').length;
  const overflows = !!overflow && fitLen < windowLen;
  if (overflows) {
    const parts = rich ? splitStoryHtml(rich, incoming, overflow, opts) : null;
    return { overflow, overflowHtml: parts ? parts.overflowHtml : '', fitLen, clipAt, overflows };
  }
  // everything in the window fits: the box keeps sending on exactly the words after its window …
  const tail = incoming.slice(windowLen).replace(/^\s+/, '');
  if (!tail.trim()) return { overflow: '', overflowHtml: '', fitLen, clipAt, overflows: false };
  // … unless some of them fit back in (the room a deletion left): they return to the editable (CanvasObject applies
  // `pull` with storyEdit.appendStoryFragment) and the next box gives them up at once
  if (pullTo > windowLen && incoming.slice(windowLen, pullTo).trim()) {
    const pulled = pullBack(win, incoming, windowLen, pullTo, rich, opts);
    if (pulled) return { ...pulled, fitLen: pullTo, clipAt: pullTo, overflows: false };
  }
  const parts = rich ? splitStoryHtml(rich, incoming, tail, opts) : null;
  return { overflow: tail, overflowHtml: parts ? parts.overflowHtml : '', fitLen, clipAt, overflows: false };
};

/**
 * The pull-back of liveFlowOf: the words of `incoming` between the window's end (`windowLen`) and `pullTo` fit back
 * into the edited box. Returns the new flow plus `pull` = { fragmentHtml, midBlock, joiner, tail } — the fragment to
 * append to the editable (its first block continues the editable's last paragraph when `midBlock`, `joiner` being the
 * whitespace between them) and `tail` = { tailHtml | tailPlain, midBlock, joiner }, the box's new window/tail boundary
 * for the edit window's bookkeeping. null when the current tail cannot be split (the flow then stays as it is).
 */
const pullBack = (win, incoming, windowLen, pullTo, rich, opts) => {
  const newOverflow = incoming.slice(pullTo).replace(/^\s+/, '');
  const curTailHtml = win.kind === 'head' ? win.tailHtml : win.rich ? win.rich.tailHtml : '';
  const curMid = win.kind === 'head' ? !!win.midBlock : win.rich ? !!win.rich.mid2 : !/\n/.test(win.joiner || '');
  const curJoiner = (win.kind === 'head' ? win.joiner : win.rich ? win.rich.joiner2 : win.joiner) || ' ';
  const parts = rich ? splitStoryHtml(rich, incoming, newOverflow, opts) : null; // what flows on now (formatted)
  if (curTailHtml) {
    const tailPlain = htmlToText(curTailHtml);
    const k = newOverflow ? storyPrefix(tailPlain, newOverflow).length : tailPlain.length;
    const cut = splitHtmlAt(curTailHtml, k, opts);
    if (!cut || !htmlToText(cut.prefixHtml).trim()) return null;
    return { overflow: newOverflow, overflowHtml: parts ? parts.overflowHtml : cut.tailHtml, pull: { fragmentHtml: cut.prefixHtml, midBlock: curMid, joiner: curJoiner, tail: { tailHtml: cut.tailHtml, midBlock: !!newOverflow && cut.midBlock, joiner: cut.joiner } } };
  }
  // plain window: the fragment is plain text
  const words = incoming.slice(windowLen, pullTo);
  const between = (incoming.slice(0, pullTo).match(/\s+$/) || [newOverflow ? ' ' : ''])[0];
  const fragment = words.replace(/^\s+/, '').replace(/\s+$/, '');
  if (!fragment) return null;
  return { overflow: newOverflow, overflowHtml: parts ? parts.overflowHtml : '', pull: { fragmentHtml: textToHtml(fragment), midBlock: curMid, joiner: curJoiner, tail: { tailPlain: newOverflow, midBlock: !!newOverflow && !/\n/.test(between), joiner: newOverflow ? between : '' } } };
};

/** What flows *into* a box: the story itself for the head, the previous box's overflow otherwise. */
export const incomingTextOf = (box, textFlow = {}) => {
  const p = box?.properties || {};
  return p.linkPrevId ? textFlow[p.linkPrevId] ?? '' : storyPlainText(p);
};

/**
 * Flows for the boxes that are not rendered right now. Walks every story head-first: boxes in `renderedIds` are
 * trusted to the canvas (their recorded flow is used; when it is not recorded yet the walk stops and resumes on the
 * next pass), every other box is measured here. Returns { id: overflow } for entries that differ from `textFlow`.
 */
export const offPageFlows = (pages, scratch, textFlow = {}, renderedIds = new Set()) => {
  const objects = documentObjects(pages, scratch);
  const byId = new Map(objects.map((o) => [o.id, o]));
  const updates = {};
  storyHeads(objects).forEach((head) => {
    let incoming = storyPlainText(head.properties || {});
    let incomingHtml = ''; // the head measures its own (rich) text; from then on the formatted overflow travels along
    let box = head;
    const seen = new Set();
    while (box && !seen.has(box.id)) {
      seen.add(box.id);
      const p = box.properties || {};
      let out, outHtml;
      if (renderedIds.has(box.id)) {
        if (!(box.id in textFlow) && p.linkNextId) return; // the canvas has not measured this one yet
        out = textFlow[box.id] || '';
        outHtml = flowHtmlOf(textFlow, box.id);
      } else {
        ({ overflow: out, overflowHtml: outHtml } = measureBoxFlow(box, incoming, { obstacles: obstaclesOfBox(box, pages, scratch), incomingHtml }));
        if (!p.linkNextId && !p.linkPrevId) { out = ''; outHtml = ''; } // (unreachable for a chain member; keeps the contract of a lone box)
        if ((textFlow[box.id] || '') !== out) updates[box.id] = out;
        if (flowHtmlOf(textFlow, box.id) !== outHtml) updates[flowHtmlKey(box.id)] = outHtml;
      }
      incoming = out;
      incomingHtml = outHtml;
      box = p.linkNextId ? byId.get(p.linkNextId) : null;
    }
  });
  return updates;
};

/** The box's rectangle kept inside the page (same size when it fits, otherwise shrunk to the page). */
const fitRectToPage = (box, page) => {
  const width = Math.min(box.width, page.width), height = Math.min(box.height, page.height);
  const x = Math.max(0, Math.min(box.x, page.width - width));
  const y = Math.max(0, Math.min(box.y, page.height - height));
  return { x, y, width, height };
};

export const AUTOFLOW_MAX_PAGES = 60;

/**
 * Empty text boxes the story could flow into before new pages are added (Publisher asks "flow into existing boxes?"):
 * unlinked, unlocked, visible, no text, no WordArt — on the tail's page below / right of the tail, then on every later
 * page top-to-bottom, left-to-right. Scratch-area boxes are skipped (they are not on a page).
 */
export const emptyFlowTargets = (pages, tail, baseIndex) => {
  const out = [];
  pages.forEach((pg, pi) => {
    if (pi < baseIndex) return;
    (pg.objects || [])
      .filter((o) => isTextType(o) && o.id !== tail.id && o.visible !== false && !o.locked && !o.properties?.linkPrevId && !o.properties?.linkNextId && !storyPlainText(o.properties || {}).trim() && !hasWordArtTransform(o.properties || {}))
      .sort((a, b) => (a.y - b.y) || (a.x - b.x))
      .forEach((o) => {
        const after = pi > baseIndex || o.y > tail.y + 1 || (Math.abs(o.y - tail.y) <= 1 && o.x > tail.x);
        if (after) out.push({ box: o, pageIndex: pi });
      });
  });
  return out;
};

/**
 * Plan an Autoflow from `tailId` (the last box of a story, or a lone text box): new pages after the tail's page, each
 * with one linked text box of the same size and position as the tail, until the whole story fits (or the safety cap).
 * Pure apart from DOM measurement — the reducer applies the result (AUTOFLOW_STORY).
 * Returns null when the box cannot be flowed, { additions: [], overflowWords: 0 } when everything already fits.
 */
export const planAutoflow = ({ pages, scratch, textFlow = {}, currentPageIndex = 0 }, tailId, { maxPages = AUTOFLOW_MAX_PAGES, pageNamer, useExisting = true } = {}) => {
  const objects = documentObjects(pages, scratch);
  const tail = objects.find((o) => o.id === tailId);
  if (!tail || !isTextType(tail) || tail.properties?.linkNextId) return null;
  const incoming = incomingTextOf(tail, textFlow);
  let { overflow, overflowHtml } = measureBoxFlow(tail, incoming, { obstacles: obstaclesOfBox(tail, pages, scratch), incomingHtml: tail.properties?.linkPrevId ? flowHtmlOf(textFlow, tail.properties.linkPrevId) : '' });
  const overflowWords = (overflow.trim().match(/\S+/g) || []).length;
  const loc = locateObject(tailId, pages, scratch);
  const baseIndex = loc && !loc.scratch ? loc.pageIndex : currentPageIndex;
  const candidates = emptyFlowTargets(pages, tail, baseIndex);
  if (!overflow.trim()) return { tailId, chain: [tailId], fills: [], additions: [], flows: {}, overflowWords: 0, lastBoxId: tailId, candidates: candidates.length };
  const basePage = pages[baseIndex] || pages[0];
  const rect = fitRectToPage(tail, basePage);
  const additions = [];
  const flows = {};
  const boxes = [];
  const fills = [];
  let prev = tail;
  // each box is measured with the formatted overflow of the one before (as the canvas will show it), the plan records the plain flows
  const step = (box) => { ({ overflow, overflowHtml } = measureBoxFlow(box, overflow, { obstacles: obstaclesOfBox(box, pages, scratch), incomingHtml: overflowHtml })); };
  // 1. existing empty boxes after the tail, in reading order (Publisher: "autoflow into existing text boxes?")
  if (useExisting) {
    for (const { box, pageIndex } of candidates) {
      if (!overflow.trim()) break;
      fills.push({ id: box.id, pageIndex });
      flows[prev.id] = overflow;
      step(box);
      prev = box;
    }
  }
  // 2. new pages after the last box used, each with a linked box the size of the tail
  let insertAt = (fills.length ? fills[fills.length - 1].pageIndex : baseIndex) + 1;
  const namer = pageNamer || ((n) => `Page ${n}`);
  while (overflow.trim() && additions.length < maxPages) {
    const box = {
      ...tail,
      id: uuidv4(),
      ...rect,
      rotation: 0,
      layer: 0,
      locked: false,
      visible: true,
      properties: { ...(tail.properties || {}), text: '', html: undefined, linkPrevId: prev.id, linkNextId: null, hyperlink: undefined },
    };
    const page = {
      id: uuidv4(),
      name: namer(pages.length + additions.length + 1),
      width: basePage.width,
      height: basePage.height,
      masterId: basePage.masterId || 'default',
      background: { type: 'solid', color: '#ffffff' },
      objects: [box],
    };
    additions.push({ insertAt, page });
    boxes.push(box);
    flows[prev.id] = overflow; // what leaves `prev` and arrives in this box
    step(box);
    prev = box;
    insertAt += 1;
  }
  // forward links between the new boxes (the reducer re-applies the whole chain anyway)
  boxes.forEach((b, i) => { if (boxes[i + 1]) b.properties.linkNextId = boxes[i + 1].id; });
  if (overflow.trim()) flows[prev.id] = overflow; // still overflowing after the cap: the last box records it
  const chain = [tailId, ...fills.map((f) => f.id), ...boxes.map((b) => b.id)];
  return { tailId, chain, fills, additions, flows, overflowWords, lastBoxId: prev.id, capped: !!overflow.trim(), firstNewBoxId: boxes[0]?.id || null, candidates: candidates.length };
};
